import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@cbhq/cds-web/layout';
import { Spinner } from '@cbhq/cds-web/loaders';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { useExperiments } from '../../../utils/experiments';
import { useManagePermissionsNow } from '../hooks/useManagePermissionsNow';
import { useWorkspaceTeam } from '../hooks/useWorkspaceTeam';
import { canManageMembers } from '../utils/canManageMembers';
import { ROUTE_TEAM } from '../utils/constants';

import { MultiLEInviteTray } from './MultiLEInviteTray';
import { SingleLEInviteTray } from './SingleLEInviteTray';

export function Invite() {
  const navigate = useNavigate();
  const location = useLocation();

  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? '';
  // The role pickers lock implied roles only under t0018_4fba688a, so the trays
  // mount once CIFER assignments have loaded and a selection can't predate them.
  const { isLoaded: areExperimentsLoaded } = useExperiments();

  // canManage reads false until both the members and the user profile load.
  const { currentMember: currentUser, isLoaded: isTeamLoaded } = useWorkspaceTeam(organizationId);
  // IDFE-4022: an admin role still in its grace period doesn't grant manage controls.
  const managePermissionsNow = useManagePermissionsNow(currentUser, organizationId);
  const canManage = useMemo(
    () => canManageMembers(currentUser, managePermissionsNow),
    [currentUser, managePermissionsNow],
  );

  useEffect(() => {
    if (isTeamLoaded && !canManage) {
      navigate({ pathname: ROUTE_TEAM, search: location.search }, { replace: true });
    }
  }, [canManage, isTeamLoaded, navigate, location.search]);

  const handleClose = useCallback(() => {
    navigate({
      pathname: ROUTE_TEAM,
      search: location.search,
    });
  }, [navigate, location.search]);

  const legalEntityCount = (selectedOrganization?.legalEntities ?? []).length;
  const isMultiLE = legalEntityCount > 1;

  if (!areExperimentsLoaded) {
    return (
      <Box alignItems="center" justifyContent="center" padding={6}>
        <Spinner size={4} color="fgPrimary" />
      </Box>
    );
  }

  return isMultiLE ? (
    <MultiLEInviteTray onClose={handleClose} />
  ) : (
    <SingleLEInviteTray onClose={handleClose} />
  );
}
